import { useEffect, useRef } from 'react';
import { dismissAlertViaCookie as dismissEmailConfirmationAlertViaCookie } from 'platform/mhv/components/MhvAlertConfirmEmail/selectors';
import { FIELD_NAMES } from '../constants';
import {
  isPendingTransaction,
  isSuccessfulTransaction,
} from '../util/transactions';

export const DEFAULT_POLL_TIMEOUT = 2000;

/**
 * The edit view's transaction lifecycle: poll a pending transaction until it
 * resolves, and dismiss the MHV email confirmation alert once an email
 * transaction succeeds.
 *
 * PORT NOTE: the class called this.props.refreshTransaction(transaction,
 * analyticsSectionName) itself. Here the caller passes onRefresh, so the hook
 * needs neither the dispatch prop nor analyticsSectionName.
 */
export const useTransactionPolling = ({
  transaction,
  transactionRequest,
  fieldName,
  onRefresh,
}) => {
  const intervalRef = useRef(null);
  const onRefreshRef = useRef(onRefresh);
  const wasPendingRef = useRef(false);
  const unmountValuesRef = useRef({ fieldName, transactionRequest });

  const isPending = isPendingTransaction(transaction);

  // Keep the latest callback and unmount inputs available without making them
  // effect dependencies.
  useEffect(() => {
    onRefreshRef.current = onRefresh;
    unmountValuesRef.current = { fieldName, transactionRequest };
  });

  // Start polling when the transaction becomes pending; stop when it resolves
  // or the component unmounts.
  //
  // PORT NOTE (bug fix): the class only started the interval on the
  // not-pending -> pending transition inside componentDidUpdate, which never
  // runs for the first render. A field opened while a transaction was already
  // pending in the store — reopening the edit view after closing it mid-save —
  // therefore never polled, and the transaction stayed pending until something
  // else refreshed it. Keying the effect on isPending rather than on a
  // transition covers the mount case too.
  //
  // PORT NOTE (bug fix): the class read window.VetsGov.pollTimeout unguarded
  // and threw when window.VetsGov was undefined.
  useEffect(() => {
    if (!isPending) {
      return undefined;
    }

    intervalRef.current = window.setInterval(
      () => onRefreshRef.current(),
      window.VetsGov?.pollTimeout || DEFAULT_POLL_TIMEOUT,
    );

    return () => {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isPending]);

  // Dismiss the MHV email confirmation alert when an email transaction
  // finishes successfully. Handles the component staying mounted after the
  // transaction completes.
  useEffect(() => {
    if (
      wasPendingRef.current &&
      !isPending &&
      fieldName === FIELD_NAMES.EMAIL &&
      transaction &&
      isSuccessfulTransaction(transaction)
    ) {
      dismissEmailConfirmationAlertViaCookie();
    }

    wasPendingRef.current = isPending;
  }, [isPending, fieldName, transaction]);

  // Fallback for the case where the component unmounts immediately after the
  // transaction completes, so the effect above never runs. Skipped when the
  // request errored — that path resets the transaction request instead.
  useEffect(() => {
    return () => {
      const {
        fieldName: currentFieldName,
        transactionRequest: currentTransactionRequest,
      } = unmountValuesRef.current;

      if (
        !currentTransactionRequest?.error &&
        currentFieldName === FIELD_NAMES.EMAIL &&
        currentTransactionRequest &&
        !currentTransactionRequest.isPending
      ) {
        dismissEmailConfirmationAlertViaCookie();
      }
    };
  }, []);
};
